'use strict';
/* ==========================================================================
   levels/title.js — the title: a newspaper front page being set (DESIGN.md §2, the print shop). On the kraft desk a
   sheet of newsprint: the masthead (Civic.masthead), the headline "RACE TO 270" set in wood type letter by letter
   (Civic.woodtype, from lesson.json's title), the deck line (the subtitle, typewriter), and the front-page picture: the
   U.S. map (core-local/mapkit.js) being inked state by state, west to east, into the real-numbers 269–269 split of
   College.tie() (2020 census), with the brass counter above it climbing to 269 and 269 and its 270 mark (College.need)
   left open. Nobody wins on the title. Rex (left) and Judge Okafor (right) rise in; ▶ Play → the map.
   Structure copied from lessons/ratify/levels/title.js (the docked portrait title, the loading screen).
   Pure in time: what is inked is a function of T − enteredAt (the map's ink table is brought up to date each frame,
   only when it changes). On a phone held upright the controls move to the portrait dock (Dock.title).
   ========================================================================== */
(function () {
  const PLAY = { x: 960, y: 1004 };
  const MAPBOX = { x: 560, y: 520, w: 800, h: 410 };
  const INK0 = 1.4, STEP = 0.055;                      // the first state inks at t0 + INK0, then one every STEP s
  const CV = () => window.Civic;
  let ORDER = null;                                    // [[code, 'lantern' | 'anchor'], …] west to east (College.tie())

  function order() {
    if (ORDER || !MapKit.ready()) return ORDER;
    const tie = College.tie();
    ORDER = Object.keys(tie.assign).map(st => [st, tie.assign[st]]).sort((a, b) => MapKit.info(a[0]).anchor.x - MapKit.info(b[0]).anchor.x);
    return ORDER;
  }

  class Title extends Scene {
    enter() {
      this.started = false; this.k = -1;
      this.map = MapKit.map({ box: MAPBOX, strip: 'off', picker: false, keyboard: false, labels: true });
      MapKit.load().then(() => this.map && this.map.prewarm());
    }
    exit() { this.map = null; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    /* bring the map's inks up to time t (only when the count changes) */
    inkTo(t) {
      const O = order(); if (!O || !this.map) return;
      const k = Math.max(0, Math.min(O.length, Math.floor((t - INK0) / STEP) + 1));
      if (k === this.k) return;
      if (k < this.k) this.map.reset();
      for (let i = Math.max(0, this.k); i < k; i++) this.map.set(O[i][0], O[i][1], { press: i === k - 1 });
      if (k > this.k && k > 0 && this.k >= 0 && (k % 6 === 0) && window.Audio) Audio.play('ticker', { gain: 0.25 });
      this.k = k;
    }

    draw() {
      UI.allLessons();                       // "← All lessons" (a paper tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {}, Cv = CV();
      // the desk and the sheet of newsprint
      g.fillStyle = paperTile(MapKit.INK.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      paperRect(960, 548, 1300, 1040, '#f3ecd9', { seed: 31, rot: -0.004, depth: 1.8, boil: false });
      if (IMG.tex_news) texScrap('tex_news', 960, 600, 1180, 860, { seed: 32, depth: 0, alpha: 0.12, torn: false });
      Cv.masthead('THE CIVIC PRESS', 960, 92, 1180, { size: 70, date: 'Election edition', price: 'Two inks, no parties', t0: t0 + 0.1, color: '#2b2420' });
      // the headline, set letter by letter
      Cv.woodtype(String(L.title || 'Race to 270').toUpperCase(), 960, 252, 104, { color: '#f3ead7', ink: '#2b2420', t0: t0 + 0.3, seed: 270, stagger: 0.07, gap: 0.05 });
      // the deck line (typewriter), wrapped to the column
      const sub = L.subtitle || '', font = "40px 'Special Elite'";
      MapKit._wrap(sub, 1120, font).forEach((ln, i) => {
        const a = Math.max(0, Math.min(1, (T - (t0 + 1.0 + i * 0.25)) / 0.35));
        if (a > 0) { g.save(); g.globalAlpha = a; MapKit._fitText(ln, 960, 342 + i * 48, 40, 1140, { font: "'Special Elite', monospace", color: '#2b2420' }); g.restore(); }
      });
      // the front-page picture: the map being inked, the counter above it
      this.inkTo(T - t0);
      if (MapKit.ready() && this.map) {
        const ty = College.tally(this.map.assign(), 2020);
        MapKit.tally(960, MAPBOX.y - 66, 860, ty, { h: 74, id: 'title' });
        this.map.draw();
        if (!UI.touch()) { g.save(); g.font = "22px 'Special Elite', monospace"; g.textAlign = 'center'; g.fillStyle = '#5b5446';
        g.fillText('A made-up night: the Lantern and Anchor tickets, real numbers (2020 census).', 960, MAPBOX.y + MAPBOX.h + 20);
        g.restore(); }   // (touch: the bigger ▶ Play sits there)
      }
      // Rex (left) and the Judge (right) rise in from below
      const riseR = E.outBack(P(T, t0 + 0.5, t0 + 1.0), 1.4), riseJ = E.outBack(P(T, t0 + 0.7, t0 + 1.2), 1.4);
      Puppet.draw('rex', 230, 1080 + (1 - riseR) * 600, 660, { facing: 1, t: T });
      Puppet.draw('judge', 1700, 1080 + (1 - riseJ) * 600, 720, { facing: -1, t: T });

      // portrait phone: the controls are in the dock (core/dock.js Dock.title); the stage keeps only the art
      if (UI.docked()) { Dock.title({ onStart: () => this.go(), onContinue: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 360, h: 104, color: '#9e2b25', font: "'Luckiest Guy'", size: 58, rot: -0.015 })) this.go();
      if (t > 2.4) hand(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x + 300 + (UI.tap(104) - 104) / 2, PLAY.y + 6, UI.text(32), t0 + 2.4, { color: 'rgba(43,36,32,0.75)', seed: 61, align: 'left' });
    }
  }
  Title.PLAY = PLAY;                         // tools/integration.js clicks it

  /* loading screen: no textures yet → plain newsprint, a composing stick setting type letter by letter (ratify's) */
  Title.loading = function () {
    g.fillStyle = '#efe6d2'; g.fillRect(0, 0, Game.W, Game.H);
    const word = 'SETTING TYPE', n = word.length, k = Math.floor((T * 6) % (n + 4));
    g.save(); g.font = "72px 'Alfa Slab One', Georgia, serif"; g.textAlign = 'center'; g.textBaseline = 'middle';
    const w = 74, x0 = 960 - (n * w) / 2 + w / 2;
    for (let i = 0; i < Math.min(k, n); i++) {
      if (word[i] === ' ') continue;
      g.fillStyle = '#2b2420'; g.fillRect(x0 + i * w - 32, 440, 64, 84);
      g.fillStyle = '#f3ead7'; g.fillText(word[i], x0 + i * w, 486);
    }
    g.restore();
    ink([[560, 560], [1360, 560]], { w: 6, color: '#9e2b25', seed: 7, passes: 1, step: 40 });
    hand('printing the front page…', 960, 650, 64, 0, { dur: 0.01, seed: 5, color: '#2b2420' });
  };

  Levels.title = Title;
})();
